import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { catchError, forkJoin, of, switchMap } from 'rxjs';
import { JobsService } from '../../services/jobs.service';
import { JobRequestsService } from '../../services/job-requests.service';
import { DesignService } from '../../services/design.service';
import { BrineFormulationService } from '../../services/brine-formulation.service';
import { JobDetailDto } from '../../models/job-detail-dto.interface';
import { JobRequestReportDto } from '../../models/job-request-report-dto.interface';
import { BrineFormulationDto } from '../../models/brine-formulation-dto.interface';
import {
  ContentFieldDto,
  DesignIterationDto,
  DesignTestInputDto,
  DesignTestOutputDto,
  FormulationComponentDto,
  JobRequestIterationsDto
} from '../../models/job-request-iterations-dto.interface';

interface WorksheetJobRequest {
  name: string | null;
  description: string | null;
  targetDensity: number | null;
  yieldPointFrom: number | null;
  yieldPointTo: number | null;
  pipeDiameter: number | null;
  mudPurpose: { alias: string | null } | null;
}

interface WorksheetMaterial {
  id: number;
  name: string | null;
  specificGravity: number;
  materialBarcode: string | null;
  location: string | null;
}

interface WorksheetDesignMaterial {
  id: number;
  formulationComponentId: number | null;
  weight: number;
  weightIncrease: number | null;
}

interface WorksheetPycnometer {
  specificGravity: number | null;
  density: number | null;
}

interface WorksheetRheologyResult {
  temperature: number | null;
  outPut: Record<string, number | null>;
}

interface WorksheetRheology {
  results: WorksheetRheologyResult[];
}

interface WorksheetElectricalStability {
  averageFor80: number | null;
  averageFor120: number | null;
  averageFor150: number | null;
}

interface WorksheetBrineFormulation {
  brineVolumeMl: number | null;
  waterGrams: number | null;
  saltGrams: number | null;
}

interface WorksheetDesign {
  id: number;
  name: string | null;
  designMaterials: WorksheetDesignMaterial[];
  shippingMultiplicator: number;
  pycnometer: WorksheetPycnometer | null;
  rheology: WorksheetRheology | null;
  electricalStability: WorksheetElectricalStability | null;
}

interface WorksheetViewModel {
  title: string;
  showBrineFormulation: boolean;
  description: string | null;
  jobRequest: WorksheetJobRequest;
  projectName: string | null;
  rig: { name: string | null };
  well: { name: string | null; prospect: string | null; lease: string | null };
  mud: { sampledOn: string | null };
  material: WorksheetMaterial[];
  dilutionFluidMaterials: WorksheetMaterial[];
  design: WorksheetDesign[];
  mudCheck: {
    pycnometer: WorksheetPycnometer | null;
    rheology: WorksheetRheology | null;
    electricalStability: WorksheetElectricalStability | null;
  } | null;
  brineFormulation: WorksheetBrineFormulation;
  rheologyPropertyNames: Array<{ title: string; name: string; isCalculated?: boolean }>;
}

interface RheologyProperty {
  key: string;
  aliases: string[];
}

@Component({
  selector: 'app-job-request-work-sheet',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule],
  templateUrl: './job-request-work-sheet.component.html',
  styleUrl: './job-request-work-sheet.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JobRequestWorksheetComponent implements OnInit, OnDestroy {
  @ViewChild('worksheetDocument') private worksheetDocument?: ElementRef<HTMLElement>;

  protected readonly worksheet = signal<WorksheetViewModel | null>(null);
  protected readonly isLoading = signal(true);
  protected readonly notFound = signal(false);
  protected readonly isGeneratingPdf = signal(false);
  protected readonly pdfError = signal(false);
  protected readonly pdfPreviewUrl = signal<SafeResourceUrl | null>(null);
  protected readonly pdfDownloadUrl = signal<string | null>(null);
  protected readonly pdfFilename = signal('job-request-worksheet.pdf');
  protected readonly extraColumns = Array.from({ length: 5 });

  private readonly route = inject(ActivatedRoute);
  private readonly jobsService = inject(JobsService);
  private readonly jobRequestsService = inject(JobRequestsService);
  private readonly designService = inject(DesignService);
  private readonly brineFormulationService = inject(BrineFormulationService);
  private readonly sanitizer = inject(DomSanitizer);

  private readonly rheologyProperties: RheologyProperty[] = [
    { key: 'viscosity600Rpm', aliases: ['viscosity600rpm', 'rpm600'] },
    { key: 'viscosity300Rpm', aliases: ['viscosity300rpm', 'rpm300'] },
    { key: 'viscosity200Rpm', aliases: ['viscosity200rpm', 'rpm200'] },
    { key: 'viscosity100Rpm', aliases: ['viscosity100rpm', 'rpm100'] },
    { key: 'viscosity6Rpm', aliases: ['viscosity6rpm', 'rpm6'] },
    { key: 'viscosity3Rpm', aliases: ['viscosity3rpm', 'rpm3'] },
    { key: 'gel10Sec', aliases: ['gel10sec', 'gel10s'] },
    { key: 'gel10Min', aliases: ['gel10min', 'gel10m'] },
    { key: 'gel30Min', aliases: ['gel30min', 'gel30m'] }
  ];

  ngOnInit(): void {
    const jobId = Number(this.route.snapshot.paramMap.get('jobId'));
    const requestId = Number(this.route.snapshot.paramMap.get('requestId'));

    if (!Number.isInteger(jobId) || jobId <= 0 || !Number.isInteger(requestId) || requestId <= 0) {
      this.notFound.set(true);
      this.isLoading.set(false);
      return;
    }

    this.jobsService.getJobById(jobId).pipe(
      switchMap(job => {
        const mudCheckId = job.mudCheckJobRequests?.[0]?.id;
        const hasSeparateMudCheck = mudCheckId != null && mudCheckId !== requestId;

        return forkJoin({
          job: of(job),
          report: this.jobRequestsService.getReport(requestId),
          iterations: this.designService.getIterations(requestId),
          brineFormulation: this.brineFormulationService.getByJobRequest(requestId).pipe(
            catchError(() => of(null))
          ),
          mudReport: hasSeparateMudCheck
            ? this.jobRequestsService.getReport(mudCheckId)
            : of(null),
          mudIterations: hasSeparateMudCheck
            ? this.designService.getIterations(mudCheckId)
            : of(null)
        });
      })
    ).subscribe({
      next: ({ job, report, iterations, brineFormulation, mudReport, mudIterations }) => {
        if (!report) {
          this.notFound.set(true);
          this.isLoading.set(false);
          return;
        }

        const mappedWorksheet = this.buildWorksheet(
          job,
          report,
          iterations,
          mudReport,
          mudIterations,
          brineFormulation
        );
        this.worksheet.set(mappedWorksheet);
        this.pdfFilename.set(this.createFilename(mappedWorksheet));
        this.isLoading.set(false);
        this.queuePdfGeneration();
      },
      error: () => {
        this.notFound.set(true);
        this.isLoading.set(false);
      }
    });
  }

  ngOnDestroy(): void {
    this.revokePdfUrl();
  }

  protected regeneratePdf(): void {
    this.revokePdfUrl();
    this.pdfPreviewUrl.set(null);
    this.pdfDownloadUrl.set(null);
    this.queuePdfGeneration();
  }

  protected openPdf(): void {
    const url = this.pdfDownloadUrl();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  protected materialWeight(material: WorksheetDesignMaterial, design: WorksheetDesign): number {
    return (material.weightIncrease ?? material.weight) * design.shippingMultiplicator;
  }

  protected hasWeightIncrease(material: WorksheetDesignMaterial): boolean {
    return material.weightIncrease !== null && material.weightIncrease !== undefined;
  }

  protected hasPositiveWeightIncrease(material: WorksheetDesignMaterial): boolean {
    return material.weightIncrease != null && material.weightIncrease >= 0;
  }

  private queuePdfGeneration(): void {
    this.pdfError.set(false);
    this.isGeneratingPdf.set(true);

    // Wait until Angular has rendered the signal-driven worksheet before capturing it.
    requestAnimationFrame(() => requestAnimationFrame(() => void this.generatePdf()));
  }

  private async generatePdf(): Promise<void> {
    const source = this.worksheetDocument?.nativeElement;
    if (!source) {
      this.isGeneratingPdf.set(false);
      this.pdfError.set(true);
      return;
    }

    try {
      source.classList.add('pdf-capture');
      await document.fonts.ready;
      const { default: html2pdf } = await import('html2pdf.js');

      const blob = await html2pdf()
        .set({
          margin: 0.25,
          filename: this.pdfFilename(),
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            backgroundColor: '#ffffff',
            logging: false,
            scale: 2,
            useCORS: true
          },
          jsPDF: {
            unit: 'in',
            // Explicit US Letter dimensions, landscape: 11 x 8.5 inches.
            format: [11, 8.5],
            orientation: 'landscape'
          }
        })
        .from(source)
        .toPdf()
        .outputPdf('blob') as Blob;

      this.revokePdfUrl();
      const objectUrl = URL.createObjectURL(blob);
      this.pdfDownloadUrl.set(objectUrl);
      this.pdfPreviewUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl));
    } catch {
      // Keep the HTML worksheet visible as a fallback and allow the user to retry.
      this.pdfError.set(true);
    } finally {
      source.classList.remove('pdf-capture');
      this.isGeneratingPdf.set(false);
    }
  }

  private revokePdfUrl(): void {
    const currentUrl = this.pdfDownloadUrl();
    if (currentUrl) {
      URL.revokeObjectURL(currentUrl);
    }
  }

  private createFilename(worksheet: WorksheetViewModel): string {
    const baseName = `${worksheet.projectName ?? worksheet.jobRequest.name ?? 'job'}-${worksheet.title}`
      .replace(/[^a-z0-9-_]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase();

    return `${baseName || 'job-request-worksheet'}.pdf`;
  }

  private buildWorksheet(
    job: JobDetailDto,
    report: JobRequestReportDto,
    iterations: JobRequestIterationsDto | null,
    mudReport: JobRequestReportDto | null,
    mudIterations: JobRequestIterationsDto | null,
    brineFormulation: BrineFormulationDto | null
  ): WorksheetViewModel {
    const components = iterations?.formulationComponents ?? [];
    const requestedDesignIds = this.route.snapshot.queryParamMap.getAll('designid')
      .map(value => Number(value))
      .filter(value => Number.isInteger(value) && value > 0);
    const designs = (iterations?.designs ?? [])
      .filter(design => requestedDesignIds.length === 0 || requestedDesignIds.includes(design.id))
      .map(design => this.mapDesign(design, components));
    const mudDesign = mudIterations?.designs?.[0];
    const mappedMudDesign = mudDesign ? this.mapDesign(mudDesign, mudIterations.formulationComponents) : null;
    const fallbackMudPycnometer = mudReport?.originalDensity != null
      ? {
          specificGravity: mudReport.originalDensity / 8.345,
          density: mudReport.originalDensity
        }
      : null;

    return {
      title: report.jobRequestTypeId === 2 ? 'Cutback / Weigh-Up Analysis' : 'Pilot Test Analysis',
      showBrineFormulation: report.jobRequestTypeId !== 2,
      description: report.conclusion ?? report.title,
      jobRequest: {
        name: report.name,
        description: report.conclusion ?? report.title,
        targetDensity: report.targetDensity,
        yieldPointFrom: report.yieldPointFrom,
        yieldPointTo: report.yieldPointTo,
        pipeDiameter: report.pipeDiameter,
        mudPurpose: report.mudPurpose ? { alias: report.mudPurpose } : null
      },
      projectName: report.projectName ?? job.name,
      rig: { name: report.rigName ?? job.rigName ?? null },
      well: {
        name: report.wellName ?? null,
        prospect: report.wellTitle ?? null,
        lease: null
      },
      mud: { sampledOn: mudReport?.sampledOn ?? report.sampledOn },
      material: components
        .filter(component => component.parentFormulationComponentId == null)
        .map(component => this.mapMaterial(component)),
      dilutionFluidMaterials: components
        .filter(component => component.parentFormulationComponentId != null)
        .map(component => this.mapMaterial(component)),
      design: designs,
      mudCheck: {
        pycnometer: mappedMudDesign?.pycnometer ?? fallbackMudPycnometer,
        rheology: mappedMudDesign?.rheology ?? null,
        electricalStability: mappedMudDesign?.electricalStability ?? null
      },
      brineFormulation: {
        brineVolumeMl: brineFormulation?.calculation.brineVolumeMl ?? null,
        waterGrams: brineFormulation?.calculation.waterGrams ?? null,
        saltGrams: brineFormulation?.calculation.saltGrams ?? null
      },
      rheologyPropertyNames: [
        { title: '600 RPM', name: 'viscosity600Rpm' },
        { title: '300 RPM', name: 'viscosity300Rpm' },
        { title: '200 RPM', name: 'viscosity200Rpm' },
        { title: '100 RPM', name: 'viscosity100Rpm' },
        { title: '6 RPM', name: 'viscosity6Rpm' },
        { title: '3 RPM', name: 'viscosity3Rpm' },
        { title: 'PV', name: 'plasticViscosity', isCalculated: true },
        { title: 'YP', name: 'yieldPoint', isCalculated: true },
        { title: '10 Sec', name: 'gel10Sec' },
        { title: '10 Min', name: 'gel10Min' },
        { title: '30 Min', name: 'gel30Min' }
      ]
    };
  }

  private mapMaterial(component: FormulationComponentDto): WorksheetMaterial {
    return {
      id: component.id,
      name: component.name ?? component.materialName ?? component.chemicalName ?? `Component ${component.id}`,
      specificGravity: component.specificGravity,
      materialBarcode: component.materialBarcode,
      location: component.location
    };
  }

  private mapDesign(design: DesignIterationDto, components: FormulationComponentDto[]): WorksheetDesign {
    const tests = design.testGroups.flatMap(group => group.testInputs);

    return {
      id: design.id,
      name: design.name,
      designMaterials: design.materials.map(material => ({
        id: material.id,
        formulationComponentId: material.formulationComponentId,
        weight: material.weight,
        weightIncrease: null as number | null
      })),
      shippingMultiplicator: this.getShippingMultiplicator(design, components),
      pycnometer: this.mapPycnometer(this.findTest(tests, 'pycnometer')),
      rheology: this.mapRheology(this.findTest(tests, 'rheology')),
      electricalStability: this.mapElectricalStability(this.findTest(tests, 'electricalstability'))
    };
  }

  private findTest(tests: DesignTestInputDto[], testName: string): DesignTestInputDto | null {
    return tests.find(test => {
      const searchable = [test.testTypeAlias, test.testTypeName, test.title]
        .filter(value => !!value)
        .map(value => this.normalize(value!))
        .join(' ');
      return searchable.includes(testName);
    }) ?? null;
  }

  private mapPycnometer(test: DesignTestInputDto | null): WorksheetPycnometer | null {
    if (!test) return null;

    const output = this.firstOutput(test);
    const specificGravity = this.fieldNumber(output?.parsedValues, ['specificgravity', 'sg'])
      ?? this.calculatePycnometerSpecificGravity(output?.content ?? null);
    const density = this.fieldNumber(output?.parsedValues, ['density', 'ppg'])
      ?? (specificGravity != null ? specificGravity * 8.345 : null);

    return { specificGravity, density };
  }

  private mapRheology(test: DesignTestInputDto | null): WorksheetRheology | null {
    if (!test) return null;

    const output = this.firstOutput(test);
    const outputDocument = this.parseXml(output?.content ?? null);
    const inputDocument = this.parseXml(test.content);
    const outputReadings = this.findElements(outputDocument, element => {
      const name = this.normalize(element.localName ?? element.tagName);
      return name.startsWith('rheologyoutputreading') ||
        (name === 'reading' && this.normalize(outputDocument?.documentElement.localName ?? '') === 'rheologyoutput');
    });
    const inputReadings = this.findElements(inputDocument, element => {
      const name = this.normalize(element.localName ?? element.tagName);
      return name.startsWith('rheologyinputreading') ||
        (name === 'reading' && this.normalize(inputDocument?.documentElement.localName ?? '') === 'rheologyinput');
    });
    const outputsById = new Map<string, Element>();

    for (const outputReading of outputReadings) {
      const id = this.elementText(outputReading, ['id']);
      if (id) outputsById.set(this.normalize(id), outputReading);
    }

    const parsedTemperatures = test.parsedInputValues
      .filter(field => this.normalize(field.label) === 'temperature')
      .map(field => this.toNumber(field.value));
    const profileReadings = inputReadings.length > 0 ? inputReadings : outputReadings;
    const parsedValues = outputReadings.length === 0 && profileReadings.length === 1
      ? output?.parsedValues
      : undefined;
    const results = profileReadings.map((reading, index) => {
      const id = this.elementText(reading, ['id']);
      const outputReading = inputReadings.length === 0
        ? reading
        : (id && outputsById.size > 0 ? outputsById.get(this.normalize(id)) : outputReadings[index]);
      const temperature = (inputReadings.length > 0 ? this.elementNumber(reading, ['temperature']) : null)
        ?? parsedTemperatures[index]
        ?? this.elementNumber(outputReading ?? null, ['temperature']);
      const values: Record<string, number | null> = {};

      for (const property of this.rheologyProperties) {
        values[property.key] = outputReading
          ? this.elementNumber(outputReading, property.aliases)
          : this.fieldNumber(parsedValues, property.aliases);
      }

      this.addCalculatedRheologyValues(values, outputReading ?? parsedValues);
      return { temperature, outPut: values };
    });

    if (results.length > 0) return { results };

    const values: Record<string, number | null> = {};
    for (const property of this.rheologyProperties) {
      values[property.key] = this.fieldNumber(output?.parsedValues, property.aliases);
    }
    this.addCalculatedRheologyValues(values, output?.parsedValues);
    const temperature = parsedTemperatures[0] ?? null;
    return { results: [{ temperature, outPut: values }] };
  }

  private addCalculatedRheologyValues(
    values: Record<string, number | null>,
    source: Element | ContentFieldDto[] | undefined
  ): void {
    const directPlasticViscosity = source instanceof Element
      ? this.elementNumber(source, ['plasticviscosity', 'pv'])
      : this.fieldNumber(source, ['plasticviscosity', 'pv']);
    const directYieldPoint = source instanceof Element
      ? this.elementNumber(source, ['yieldpoint', 'yp'])
      : this.fieldNumber(source, ['yieldpoint', 'yp']);
    const viscosity600 = values['viscosity600Rpm'];
    const viscosity300 = values['viscosity300Rpm'];
    const plasticViscosity = directPlasticViscosity
      ?? (viscosity600 != null && viscosity300 != null ? viscosity600 - viscosity300 : null);

    values['plasticViscosity'] = plasticViscosity;
    values['yieldPoint'] = directYieldPoint
      ?? (viscosity300 != null && plasticViscosity != null ? viscosity300 - plasticViscosity : null);
  }

  private mapElectricalStability(test: DesignTestInputDto | null): WorksheetElectricalStability | null {
    if (!test) return null;

    const output = this.firstOutput(test);
    const outputDocument = this.parseXml(output?.content ?? null);
    const inputDocument = this.parseXml(test.content);
    const outputReadings = this.findElements(outputDocument, element => {
      const name = this.normalize(element.localName ?? element.tagName);
      return name.startsWith('electricalstabilityoutputreading') ||
        (name === 'reading' && this.normalize(outputDocument?.documentElement.localName ?? '') === 'electricalstabilityoutput');
    });
    const inputReadings = this.findElements(inputDocument, element => {
      const name = this.normalize(element.localName ?? element.tagName);
      return name.startsWith('electricalstabilityinputreading') ||
        (name === 'reading' && this.normalize(inputDocument?.documentElement.localName ?? '') === 'electricalstabilityinput');
    });
    const temperaturesById = new Map<string, number>();

    for (const inputReading of inputReadings) {
      const id = this.elementText(inputReading, ['id']);
      const temperature = this.elementNumber(inputReading, ['temperature']);
      if (id && temperature != null) temperaturesById.set(this.normalize(id), temperature);
    }

    const valuesByTemperature = new Map<number, number[]>();
    outputReadings.forEach((reading, index) => {
      const id = this.elementText(reading, ['id']);
      const temperature = (id ? temperaturesById.get(this.normalize(id)) : null) ?? [80, 120, 150][index] ?? 80;
      const container = this.findElements(reading, element =>
        this.normalize(element.localName ?? element.tagName) === 'dielectricbreakdownvoltages')[0];
      const values = container
        ? this.leafNumbers(container)
        : this.findElements(reading, element =>
            ['voltage1', 'voltage2', 'voltage3'].includes(this.normalize(element.localName ?? element.tagName)))
          .map(element => this.toNumber(element.textContent));
      const validValues = values.filter((value): value is number => value != null);

      if (validValues.length > 0) {
        valuesByTemperature.set(temperature, [
          ...(valuesByTemperature.get(temperature) ?? []),
          ...validValues
        ]);
      }
    });

    if (valuesByTemperature.size === 0 && output?.parsedValues) {
      const fallbackValues = output.parsedValues
        .map(field => this.toNumber(field.value))
        .filter((value): value is number => value != null);
      if (fallbackValues.length > 0) valuesByTemperature.set(80, fallbackValues);
    }

    return {
      averageFor80: this.average(valuesByTemperature.get(80)),
      averageFor120: this.average(valuesByTemperature.get(120)),
      averageFor150: this.average(valuesByTemperature.get(150))
    };
  }

  private firstOutput(test: DesignTestInputDto): DesignTestOutputDto | null {
    return test.testOutputs.find(output => !!output.content || output.parsedValues.length > 0) ?? null;
  }

  private calculatePycnometerSpecificGravity(content: string | null): number | null {
    const document = this.parseXml(content);
    if (!document) return null;

    const sampleWeight = this.elementNumber(document, ['sampleweight']);
    const cellVolume = this.elementNumber(document, ['cellvolume']);
    const referenceVolume = this.elementNumber(document, ['referencevolume']);
    if (sampleWeight == null || cellVolume == null || referenceVolume == null) return null;

    const readings = this.findElements(document, element => {
      const name = this.normalize(element.localName ?? element.tagName);
      return name === 'reading' || name.startsWith('pycnometeroutputreading');
    });
    const specificGravities = readings
      .map(reading => {
        const p1 = this.elementNumber(reading, ['p1']);
        const p2 = this.elementNumber(reading, ['p2']);
        if (p1 == null || p2 == null) return null;

        const powderVolume = p2 === 0 ? 0 : cellVolume - referenceVolume * (p1 / p2 - 1);
        return powderVolume <= 0 ? 0 : sampleWeight / powderVolume;
      })
      .filter((value): value is number => value != null && value > 0);

    return specificGravities.length > 0
      ? specificGravities.reduce((sum, value) => sum + value, 0) / specificGravities.length
      : null;
  }

  private getShippingMultiplicator(
    design: DesignIterationDto,
    components: FormulationComponentDto[]
  ): number {
    if (!design.isShipping) return 1;

    const children = components.filter(component => component.parentFormulationComponentId != null);
    const parent = components.find(component => children.some(
      child => child.parentFormulationComponentId === component.id));
    if (!parent || parent.specificGravity <= 0) return 1;

    const parentMaterial = design.materials.find(material => material.formulationComponentId === parent.id);
    if (!parentMaterial) return 1;

    const parentVolume = parentMaterial.weight / parent.specificGravity;
    const childVolume = children.reduce((total, child) => {
      const material = design.materials.find(row => row.formulationComponentId === child.id);
      return total + (material && child.specificGravity > 0 ? material.weight / child.specificGravity : 0);
    }, 0);
    if (parentVolume <= 0 || childVolume <= 0) return 1;

    const volumePercent = childVolume / parentVolume;
    return (volumePercent + 0.05) / volumePercent;
  }

  private fieldNumber(fields: ContentFieldDto[] | undefined, aliases: string[]): number | null {
    if (!fields) return null;
    const normalizedAliases = aliases.map(alias => this.normalize(alias));
    const field = fields.find(candidate => {
      const label = this.normalize(candidate.label);
      return normalizedAliases.some(alias => label === alias || label.includes(alias));
    });
    return field ? this.toNumber(field.value) : null;
  }

  private elementNumber(root: Document | Element | null, aliases: string[]): number | null {
    return this.toNumber(this.elementText(root, aliases));
  }

  private elementText(root: Document | Element | null, aliases: string[]): string | null {
    const normalizedAliases = aliases.map(alias => this.normalize(alias));
    const element = this.findElements(root, candidate => {
      const name = this.normalize(candidate.localName ?? candidate.tagName);
      return normalizedAliases.some(alias => name === alias || name.includes(alias));
    })[0];
    return element?.textContent?.trim() || null;
  }

  private leafNumbers(root: Element): Array<number | null> {
    return this.findElements(root, element => element.children.length === 0)
      .map(element => this.toNumber(element.textContent));
  }

  private parseXml(content: string | null): Document | null {
    if (!content || typeof DOMParser === 'undefined') return null;
    const parsed = new DOMParser().parseFromString(content, 'application/xml');
    return parsed.querySelector('parsererror') ? null : parsed;
  }

  private findElements(
    root: Document | Element | null,
    predicate: (element: Element) => boolean
  ): Element[] {
    if (!root) return [];
    return Array.from(root.getElementsByTagName('*')).filter(predicate);
  }

  private toNumber(value: string | null | undefined): number | null {
    if (value == null || value.trim() === '') return null;
    const parsed = Number(value.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }

  private average(values: number[] | undefined): number | null {
    if (!values || values.length === 0) return null;
    return values.reduce((sum, value) => sum + value, 0) / values.length;
  }

  private normalize(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]/g, '');
  }
}